import * as React from "react";
import { useTranslation } from "react-i18next";
import { useIsMutating } from "@tanstack/react-query";
import { Link, useRouterState } from "@tanstack/react-router";
import { useAuth } from "@app/providers/auth";
import { hasPermission } from "@shared/rbac/guards";
import { Button, Dialog } from "@ui/core";
import {
  applyAdminUpdate,
  installAdminPwa,
  useAdminPwa,
  useOnline,
  useUpdateBlocker,
} from "@shared/lib/pwa";

export function InstallAdminButton({
  menuItem = false,
}: {
  menuItem?: boolean;
}) {
  const { t } = useTranslation("common");
  const { canInstall, installed } = useAdminPwa();
  const [help, setHelp] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  if (installed) return null;
  const ios =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  return (
    <>
      <button
        type="button"
        role={menuItem ? "menuitem" : undefined}
        disabled={busy}
        className="min-h-11 rounded-md px-2 py-1.5 text-start text-sm hover:bg-primary/10 disabled:opacity-50"
        onClick={async () => {
          if (!canInstall) {
            setHelp(true);
            return;
          }
          setBusy(true);
          try {
            await installAdminPwa();
          } catch {
            setFailed(true);
            setHelp(true);
          } finally {
            setBusy(false);
          }
        }}
      >
        {t("pwa.install")}
      </button>
      <Dialog
        open={help}
        onOpenChange={setHelp}
        title={t("pwa.install")}
        description={t(
          failed
            ? "pwa.installFailed"
            : ios
              ? "pwa.iosInstructions"
              : "pwa.browserInstructions",
        )}
        footer={
          <Button onClick={() => setHelp(false)}>{t("pwa.close")}</Button>
        }
      />
    </>
  );
}

export function AdminPwaStatus() {
  const { t } = useTranslation("common");
  const { updateAvailable, updateState } = useAdminPwa();
  const mutating = useIsMutating();
  useUpdateBlocker(mutating > 0);
  const [dismissed, setDismissed] = React.useState(false);
  if (!updateAvailable || dismissed) return null;
  return (
    <aside
      role="status"
      className="fixed inset-x-3 bottom-3 z-[100] mx-auto flex max-w-lg flex-wrap items-center gap-3 rounded-xl border bg-background p-4 text-foreground shadow-lg pwa-safe-bottom"
    >
      <p className="flex-1 text-sm">
        {t(
          updateState === "blocked"
            ? "pwa.updateBlocked"
            : "pwa.updateAvailable",
        )}
      </p>
      <Button
        disabled={updateState === "checking"}
        onClick={() => void applyAdminUpdate()}
      >
        {t("pwa.update")}
      </Button>
      <Button variant="outline" onClick={() => setDismissed(true)}>
        {t("pwa.later")}
      </Button>
    </aside>
  );
}

export function AdminOfflineBoundary({
  children,
}: {
  children: React.ReactNode;
}) {
  const online = useOnline();
  const { user, accessToken, refreshToken, tenantId } = useAuth();
  const { t } = useTranslation("common");
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const blocked = !online && pathname !== "/pos" && pathname !== "/logout";
  const previouslyOnline = React.useRef(online);
  if (online) previouslyOnline.current = true;
  const canUsePos = Boolean(
    user &&
    tenantId &&
    (accessToken || refreshToken) &&
    hasPermission(user, "pos.access"),
  );
  return (
    <>
      {/* Preserve mounted editors when connectivity drops; don't boot online pages on an offline launch. */}
      <div hidden={blocked}>
        {(!blocked || previouslyOnline.current) && children}
      </div>
      {blocked && (
        <main className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-background p-6 text-center text-foreground">
          <h1 className="text-2xl font-semibold">
            {t("pwa.internetRequired")}
          </h1>
          <p className="max-w-md text-muted-foreground">
            {t(canUsePos ? "pwa.offlineAdmin" : "pwa.signInOnline")}
          </p>
          {canUsePos && (
            <Link
              to="/pos"
              className="rounded-md bg-primary px-4 py-3 text-primary-foreground"
            >
              {t("pwa.openPos")}
            </Link>
          )}
          <InstallAdminButton />
        </main>
      )}
    </>
  );
}
